import React, { useEffect, useRef, useId } from 'react';

let scriptPromise = null;

function loadTurnstileScript() {
  if (typeof window === 'undefined') return Promise.resolve();
  if (window.turnstile) return Promise.resolve();
  if (scriptPromise) return scriptPromise;
  scriptPromise = new Promise((resolve, reject) => {
    const s = document.createElement('script');
    s.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';
    s.async = true;
    s.defer = true;
    s.onload = () => resolve();
    s.onerror = () => reject(new Error('Turnstile failed to load'));
    document.head.appendChild(s);
  });
  return scriptPromise;
}

export default function TurnstileWidget({ siteKey, onToken, onExpire, className }) {
  const containerRef = useRef(null);
  const widgetIdRef = useRef(null);
  const domId = useId().replace(/:/g, '');

  useEffect(() => {
    if (!siteKey?.trim() || !containerRef.current) return undefined;

    let cancelled = false;
    loadTurnstileScript()
      .then(() => {
        if (cancelled || !containerRef.current || !window.turnstile) return;
        if (widgetIdRef.current) {
          try {
            window.turnstile.remove(widgetIdRef.current);
          } catch {
            /* ignore */
          }
        }
        widgetIdRef.current = window.turnstile.render(containerRef.current, {
          sitekey: siteKey.trim(),
          callback: (token) => onToken?.(token),
          'expired-callback': () => onExpire?.(),
          'error-callback': () => onExpire?.(),
          theme: 'auto',
        });
      })
      .catch(() => onExpire?.());

    return () => {
      cancelled = true;
      if (widgetIdRef.current && window.turnstile) {
        try {
          window.turnstile.remove(widgetIdRef.current);
        } catch {
          /* ignore */
        }
        widgetIdRef.current = null;
      }
    };
  }, [siteKey, onToken, onExpire]);

  if (!siteKey?.trim()) return null;

  return <div ref={containerRef} id={`turnstile-${domId}`} className={className} />;
}
